/**
 * Vendors & Agreements contracts (docs/02 §Vendors & agreements; ADR-04, ADR-42).
 *
 * The vendor domain is a HIERARCHY (ADR-42): `vendor_services` is the
 * authoritative superset of what a vendor can do, `vendor_properties` is where
 * it is used, and an AGREEMENT governs exactly ONE service over one scope —
 * company-wide or a named property set. That single service is half of the
 * supersession key, which is why it is required rather than a list.
 * `company_wide` stores no property rows and resolves dynamically, so "applies
 * everywhere" includes a property created next month.
 */

import { z } from 'zod';

import {
  addressInlineSchema,
  phoneEditableSchema,
  type AddressRow,
  type PhoneRow,
} from './address.ts';
import { partialUpdateSchema } from './partial-update.ts';
import type { DuplicateMatch } from './properties.ts';
import { DEFAULT_PAGE_SIZE } from './table-layouts.ts';

/**
 * Billing cadence, shared by agreements and utility accounts so the two can
 * never disagree. `semi_annual` is real and the first cut dropped it, which
 * left a legitimate value with nowhere to land.
 */
export const BILLING_FREQUENCIES = [
  'monthly',
  'quarterly',
  'semi_annual',
  'annual',
  'one_time',
] as const;
export type BillingFrequency = (typeof BILLING_FREQUENCIES)[number];

/**
 * Cadence in prose. Lives here, beside the enum, because two screens rendering
 * it from their own local maps is how "semi_annual" ends up on screen in one
 * place and "semi-annually" in another.
 */
export const BILLING_LABEL: Record<BillingFrequency, string> = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  semi_annual: 'semi-annually',
  annual: 'annually',
  one_time: 'one time',
};

/**
 * The same cadence at the START of a line — a select option, a `Fact` value.
 *
 * `BILLING_LABEL` reads mid-sentence ("billed monthly"), so three screens were
 * capitalising it themselves: `AgreementForm` inline, `UtilityAccountScreens`
 * through a locally derived map, and both with a `?? key` fallback that put
 * `semi_annual` on screen the moment the enum grew. Sentence case is a
 * VOCABULARY decision (docs/04 §2), not per-screen string surgery, so it is
 * written out here rather than computed — `charAt(0).toUpperCase()` is also
 * wrong for any label that ever starts with a non-letter.
 */
export const BILLING_LABEL_SENTENCE: Record<BillingFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-annually',
  annual: 'Annually',
  one_time: 'One time',
};

// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------

/**
 * A vendor is one thing or the other, and cannot become the other later.
 *
 * Matt, 2026-08-26: "vendors cant be a service vendor and utility vendor - and
 * each one of those vendor types are different", and on conversion: "once a
 * vendor is created a service vendor and not a utility vendor ... do not allow
 * it - vendor must go inactive (all properties and contracts unassigned and go
 * inactive) and the user must re-create the vendor as a utility vendor - this
 * prevents data migration corruption."
 *
 * v1 had exactly these two values (`service_provider` / `utility_provider`) and
 * no third; v2's `both` was the drift. ADR-53, ADR-59; a trigger in 0054
 * enforces the immutability against every caller, not only this schema.
 */
export const VENDOR_TYPES = ['service', 'utility'] as const;
export type VendorType = (typeof VENDOR_TYPES)[number];

/**
 * What a reader calls each type — both values under a "Type" column or `Fact`,
 * so the plain word is the label and nothing here was leaking.
 *
 * This one is DUPLICATION, not vocabulary: `VendorsListScreen` and
 * `VendorDetailScreen` each declared a private `TYPE_LABEL`, and the two
 * happened to agree. Gate 15 cannot see that — it catches a shadowed component
 * NAME, and a local `const TYPE_LABEL` is neither — so the two copies would have
 * gone on agreeing until somebody edited one of them.
 */
export const VENDOR_TYPE_LABEL: Record<VendorType, string> = {
  service: 'Service',
  utility: 'Utility',
};

export const vendorWriteSchema = z.object({
  name: z.string().trim().min(1).max(200),
  vendorType: z.enum(VENDOR_TYPES).default('service'),
  website: z.union([z.literal(''), z.url()]).nullish(),
  email: z.union([z.literal(''), z.email()]).nullish(),
  /** Where invoices go, when that is not the main address. Pairs with nameOnBill. */
  billingEmail: z.union([z.literal(''), z.email()]).nullish(),
  // Phone numbers and addresses are SUB-RESOURCES as of 0040 (see
  // contracts/address.ts and docs/02). 0035's office/after-hours pair was the
  // right answer for two numbers and stopped being right when the answer became
  // "any number of them, each labelled" — which is what v1's own
  // phone_numbers[] list had already concluded.
  /** Keys the accounting export. Properties had one; vendors did not (D-3). */
  yardiId: z.string().trim().max(60).nullish(),
  /** The name a utility bills under — routinely not the vendor's name (D-3). */
  nameOnBill: z.string().trim().max(200).nullish(),
  w9OnFile: z.boolean().default(false),
  coiOnFile: z.boolean().default(false),
  coiExpiration: z.iso.date().nullish(),
  /** ADR-37 — the Important Details band, same field name on every entity. */
  criticalInformation: z.string().trim().max(4000).nullish(),
  notes: z.string().trim().max(4000).nullish(),
});
export type VendorWrite = z.infer<typeof vendorWriteSchema>;

/**
 * The bypass reason is REQUIRED WHEN THE BYPASS IS USED — here, not in the
 * browser (RM-038).
 *
 * `VendorCreateScreen` has always disabled "Create anyway" until the reason
 * reaches three characters, and that was the only place the rule lived. Any
 * other caller — a second screen, a script, a replayed request — could post
 * `createAnyway: true` with no reason at all and the server would accept it and
 * audit a null justification, which is precisely the record the duplicate
 * interstitial exists to produce. A control that only a form enforces is not a
 * control; punch 5.2 adds a SECOND creation surface, so this had to move down
 * before that surface existed rather than after.
 *
 * Three characters, matching what the dialog already asked for: enough to
 * refuse an empty box or a stray keystroke, not enough to pretend the server
 * can judge whether an explanation is a good one.
 */
export const CREATE_ANYWAY_REASON_MIN = 3;

export const vendorCreateSchema = vendorWriteSchema
  .extend({
    createAnyway: z.boolean().default(false),
    createAnywayReason: z.string().trim().max(500).optional(),
  })
  // STRICT (see partialUpdateSchema): 0040 moved the phones and addresses off
  // this schema, and a client still posting `officePhone` would otherwise
  // create a vendor without it and be told it worked.
  .strict()
  .superRefine((value, ctx) => {
    if (value.createAnyway && (value.createAnywayReason ?? '').length < CREATE_ANYWAY_REASON_MIN) {
      ctx.addIssue({
        code: 'custom',
        path: ['createAnywayReason'],
        message: 'Explain why this is not a duplicate',
      });
    }
    /**
     * THIS ENDPOINT DOES NOT MAKE UTILITY VENDORS (RM-045).
     *
     * Matt's rule is unconditional: *"A new utility vendor must always be
     * created with its first utility account in the same atomic save,
     * regardless of origin... Do not permit a vendor-only utility record to be
     * created temporarily."* A plain vendor create cannot satisfy that — it has
     * no account to create — so it must refuse the type rather than quietly
     * produce the state the rule forbids. `utilityVendorWithAccountSchema` is
     * where a utility vendor comes from, and the message says so.
     *
     * The field stays in the shape rather than being omitted: a caller that
     * asks for a utility vendor here has made a real mistake and deserves to be
     * told what the right door is, which a stripped-and-ignored field cannot
     * do (§partialUpdateSchema's lesson, applied to create).
     */
    if (value.vendorType !== 'service') {
      ctx.addIssue({
        code: 'custom',
        path: ['vendorType'],
        message:
          'A utility vendor is created together with its first account — use the ' +
          'utility-with-account endpoint',
      });
    }
  });
export type VendorCreate = z.infer<typeof vendorCreateSchema>;

/**
 * `vendorType` is deliberately NOT updatable (ADR-59) — omitted from the changes
 * shape rather than ignored server-side, so a client that tries is told which
 * field it may not send instead of watching a silent no-op.
 */
export const vendorUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(vendorWriteSchema.omit({ vendorType: true })),
});

/**
 * The picker's option source (RM-020). A vendor list for CHOOSING from, not for
 * browsing: no paging, no sort controls, two columns.
 *
 * It exists because the party picker got its options from the paged list
 * endpoint, and `resolvePageSize` SNAPS an illegal size to 50 rather than
 * refusing it — deliberately, so a stale stored preference cannot break a list
 * screen. Asking for 500 therefore returned 50 vendors and looked like success.
 * A service vendor at row 51 was simply absent, and the picker then offered to
 * store their name as free text: a duplicate, unlinked answer to a question the
 * database could have answered properly.
 *
 * `complete` is the part that makes it safe. A caller must not offer free text
 * over a list it cannot vouch for, and this says so explicitly rather than
 * leaving a full page to be mistaken for the whole set.
 */
export const VENDOR_OPTIONS_LIMIT = 1000;

export const vendorOptionsQuerySchema = z.object({
  vendorType: z.enum(VENDOR_TYPES).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('active'),
});
export type VendorOptionsQuery = z.infer<typeof vendorOptionsQuerySchema>;

export interface VendorOptionRow {
  readonly id: string;
  readonly name: string;
}

export interface VendorOptionsResult {
  readonly rows: readonly VendorOptionRow[];
  /** False ⇒ more vendors exist than were returned. Do not offer free text. */
  readonly complete: boolean;
}

export const vendorListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('active'),
  vendorType: z.enum(VENDOR_TYPES).optional(),
  sort: z.enum(['name', 'vendor_type', 'status', 'coi_expiration', 'updated_at']).default('name'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  /**
   * Rows per page (X-6.3). Coerced to a legal size by the service rather than
   * rejected here, because the value arrives from a STORED preference that can
   * outlive the set it was legal in — and a 400 would then break the whole list
   * for whoever held the retired value. Absent means DEFAULT_PAGE_SIZE.
   */
  pageSize: z.coerce.number().int().optional(),
});
export type VendorListQuery = z.infer<typeof vendorListQuerySchema>;

/**
 * The DEFAULT page size for this list. One number for the whole app since
 * X-6.3 made the size a per-user choice — a second literal here would be a
 * second answer to "how many rows does a page hold when nobody chose".
 */
export const VENDOR_PAGE_SIZE = DEFAULT_PAGE_SIZE;

export interface VendorRow {
  readonly id: string;
  readonly name: string;
  readonly vendorType: VendorType;
  readonly status: 'active' | 'inactive';
  readonly coiOnFile: boolean;
  readonly coiExpiration: string | null;
  readonly activeAgreements: number;
  /**
   * The vendor's service categories, by NAME, alphabetical (ADR-42).
   *
   * "Who does HVAC" is the question the vendors list is opened with, and it was
   * unanswerable from the list: the hierarchy existed on the vendor's own page
   * and nowhere a person could scan. Names rather than ids because a list cell
   * is read, not joined — the same rule as the audit field diffs.
   */
  readonly services: readonly string[];
  readonly updatedAt: string;
  readonly version: number;
}

export interface VendorContactRow {
  readonly id: string;
  readonly name: string;
  readonly title: string | null;
  readonly email: string | null;
  readonly notes: string | null;
  readonly isPrimary: boolean;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  /** 0040: any number of numbers and addresses, each labelled. */
  readonly phones: readonly PhoneRow[];
  readonly addresses: readonly AddressRow[];
  /** What this person covers (ADR-42 §8) — empty means "the whole vendor". */
  readonly assignments: readonly ContactAssignmentRow[];
}

export type AgreementStatus = 'draft' | 'active' | 'superseded' | 'expired' | 'inactive';

export interface AgreementSummary {
  readonly id: string;
  readonly name: string;
  readonly coverageType: 'single' | 'multi' | 'company_wide';
  readonly status: AgreementStatus;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly costCents: number | null;
  readonly billingFrequency: BillingFrequency | null;
  readonly serviceName: string;
  readonly propertyCount: number;
}

export interface VendorDetail {
  readonly id: string;
  readonly name: string;
  readonly vendorType: VendorType;
  readonly website: string | null;
  readonly email: string | null;
  readonly billingEmail: string | null;
  readonly yardiId: string | null;
  readonly nameOnBill: string | null;
  readonly w9OnFile: boolean;
  readonly coiOnFile: boolean;
  readonly coiExpiration: string | null;
  readonly criticalInformation: string | null;
  readonly notes: string | null;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  readonly updatedAt: string;
  readonly canEdit: boolean;
  /**
   * create OR edit — the utility-account screens' aggregate (unchanged, F-88).
   * Agreement doors read `canCreateAgreements` instead (docs/11 F-89).
   */
  readonly canManageAgreements: boolean;
  /** agreements.create on an active service vendor: may file a new agreement here. */
  readonly canCreateAgreements: boolean;
  readonly addresses: readonly AddressRow[];
  readonly phones: readonly PhoneRow[];
  /** The vendor's service superset (ADR-42 §4) — authoritative, cascade-fed. */
  readonly services: readonly { readonly id: string; readonly name: string }[];
  /**
   * A UTILITY vendor's type set — "Duke Energy (utility type: power)". Empty for
   * a service vendor, and the two are never both populated because a vendor is
   * one thing (ADR-53).
   *
   * EVERY member, retired ones included, each with its catalog state (docs/11
   * F-88): a retired member is history an account may still name, so it is
   * shown by name and marked, never hidden — and never offered for a new
   * account. "Allowed types" for a new account are the ACTIVE members.
   */
  readonly utilityTypes: readonly {
    readonly id: string;
    readonly name: string;
    readonly active: boolean;
  }[];
  /**
   * The reviewed version of that SET (docs/11 F-88) — a fingerprint of its
   * membership rows, so every addition and removal moves it, including the
   * legacy empty-set initialization an account writer performs (who cannot
   * write `vendors.version`, which belongs to vendors.edit). The set editor
   * sends it back as `setVersion`, and the empty-set remediation as
   * `typeSetVersion`. Independent of `version`: a set save does not invalidate
   * an open Edit Vendor form, and a scalar save does not invalidate the set.
   */
  readonly utilityTypeSetVersion: string;
  readonly contacts: readonly VendorContactRow[];
  readonly agreements: readonly AgreementSummary[];
  readonly utilityAccounts: readonly UtilityAccountSummary[];
}

/**
 * A vendor duplicate match — the shared row PLUS the one fact that decides
 * whether it can be used as a parent (mailbox RM-057).
 *
 * `DuplicateMatch` itself stays type-free because properties use it too and a
 * property has no vendor type. Extending it here rather than widening it there
 * keeps the property scan honest and gives the vendor interstitial what it
 * needs: the duplicate scan is portfolio-wide by NAME, so a match may be the
 * wrong KIND of vendor, and "Use this vendor" must not be offered over a parent
 * 0081 refuses.
 */
export interface VendorDuplicateMatch extends DuplicateMatch {
  readonly vendorType: VendorType;
}

/** The create endpoint's 409 payload — the shared interstitial shape. */
export interface VendorDuplicateReview {
  readonly code: 'DUPLICATE_REVIEW';
  readonly message: string;
  readonly duplicates: readonly VendorDuplicateMatch[];
}

export const vendorContactWriteSchema = z.object({
  name: z.string().trim().min(1).max(120),
  title: z.string().trim().max(120).nullish(),
  email: z.union([z.literal(''), z.email()]).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  isPrimary: z.boolean().default(false),
  /** One of each, typed on create — see tenantContactWriteSchema for why. */
  phone: phoneEditableSchema.optional(),
  address: addressInlineSchema.optional(),
});
export type VendorContactWrite = z.infer<typeof vendorContactWriteSchema>;

export const vendorContactUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(vendorContactWriteSchema),
});

// ---------------------------------------------------------------------------
// Vendor hierarchy (ADR-42): services superset, property links, assignments
// ---------------------------------------------------------------------------

/** One cascade side effect (docs/03 §2b), phrased for the confirm dialog. */
export interface CascadeEffect {
  /**
   * `add_vendor_property_service`: the vendor is linked to a covered property
   * but the agreement's service is not recorded at that link — the third
   * hierarchy edge, which a link alone does not supply (docs/11 F-89, C3).
   */
  readonly kind:
    | 'add_vendor_service'
    | 'link_vendor_property'
    | 'add_vendor_property_service'
    | 'add_vendor_utility_type';
  readonly label: string;
}

/**
 * RETIRING A VENDOR — the portfolio-wide consequence set (punch 5.2).
 *
 * Matt, 2026-09-01: *"Treat vendor inactivation as a portfolio-wide operation,
 * regardless of which screen initiated it."* Retiring a vendor from the tab of
 * one building must not quietly retire only that building's records, and must
 * not describe itself as if it had. So this shape carries three separate
 * numbers, because they answer three different questions:
 *
 *   * `utilityAccounts` / `propertyLinks` — how many RECORDS are retired.
 *   * `propertiesAffected` — how many BUILDINGS notice, deduplicated across
 *     both kinds. One account serving seven properties is one record and seven
 *     buildings, and a confirmation that said "1" would be read as "1".
 *   * `blockingAgreements` — what the user must resolve first, and cannot be
 *     cascaded past. An agreement's status is a contractual decision; ending
 *     one because somebody retired a vendor is not this operation's to make.
 *
 * The lists carry NAMES — account numbers, agreement names, property names —
 * because a confirmation full of uuids is a confirmation nobody reads.
 */
export interface VendorRetirementEffects {
  readonly utilityAccounts: readonly {
    readonly id: string;
    readonly accountNumber: string;
    readonly typeName: string | null;
    readonly propertyNames: readonly string[];
  }[];
  readonly propertyLinks: readonly { readonly id: string; readonly propertyName: string }[];
  /** Deduplicated across accounts and links: a building reached twice counts once. */
  readonly propertiesAffected: readonly string[];
}

/**
 * The 409 body when a vendor cannot be retired yet. Separate from the confirm
 * payload because it is not a prompt: there is nothing to confirm, only work to
 * go and do. Every covered property is named — a multi-property agreement that
 * reported only its own name would leave the user guessing which buildings the
 * refusal is really about.
 */
export interface VendorRetirementBlocked {
  readonly code: 'HAS_ACTIVE_AGREEMENTS';
  readonly message: string;
  readonly agreements: readonly {
    readonly id: string;
    readonly name: string;
    readonly status: string;
    readonly coverage: string;
    readonly propertyNames: readonly string[];
  }[];
}

/** The 409 body asking the user to confirm a vendor retirement's full reach. */
export interface VendorRetirementReview {
  readonly code: 'CONFIRM_REQUIRED';
  readonly message: string;
  readonly retirement: VendorRetirementEffects;
}

/**
 * The 409 body for confirm-then-cascade (docs/03 §2b). The client renders the
 * effect labels and resubmits with `confirm: true`; the server RE-DERIVES the
 * effects on that resubmission — this list is display material, never input.
 */
export interface ConfirmRequiredPayload {
  readonly code: 'CONFIRM_REQUIRED';
  readonly message: string;
  readonly effects: readonly CascadeEffect[];
}

/** PUT /vendors/:id/services — set-based, like org-unit membership. */
export const vendorServicesSetSchema = z.object({
  serviceCategoryIds: z.array(z.uuid()).max(200),
});

/**
 * What a UTILITY vendor sells — the mirror of `vendor_services`, and the
 * authoritative superset an account's type must come from.
 *
 * Matt, 2026-08-26: "the vendor carries a set of utility types and each account
 * names which one it is - that works". Duke Energy is {Power}; a telecom is
 * {Internet, Phone} and each account says which. A set rather than one column
 * because the second case is real and costs nothing until it turns up.
 */
export const vendorUtilityTypesSetSchema = z.object({
  /**
   * The `utilityTypeSetVersion` the editor was opened on (docs/11 F-88). A
   * whole-set write replaces what is there, so it must prove it saw what is
   * there: a stale one is refused (409 VERSION_CONFLICT), never applied over
   * another editor's change.
   */
  setVersion: z.string().min(1).max(64),
  utilityTypeIds: z.array(z.uuid()).max(200),
});

export const vendorPropertyLinkSchema = z.object({
  propertyId: z.uuid(),
  /** What the vendor does THERE — a ragged subset of the superset (§5). */
  serviceCategoryIds: z.array(z.uuid()).max(200).default([]),
  notes: z.string().trim().max(2000).nullish(),
  confirm: z.boolean().default(false),
});

export const vendorPropertyUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: z.object({
    serviceCategoryIds: z.array(z.uuid()).max(200).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  }),
  confirm: z.boolean().default(false),
});

export interface VendorPropertyRow {
  readonly id: string;
  readonly propertyId: string;
  readonly propertyName: string;
  readonly city: string | null;
  readonly status: 'active' | 'inactive';
  readonly notes: string | null;
  readonly version: number;
  readonly services: readonly { readonly id: string; readonly name: string }[];
}

/** All three shapes are legal (§8): property+service, property alone, service alone. */
export const contactAssignmentWriteSchema = z.object({
  propertyId: z.uuid().nullish(),
  serviceCategoryId: z.uuid().nullish(),
  confirm: z.boolean().default(false),
});

export interface ContactAssignmentRow {
  readonly id: string;
  readonly propertyId: string | null;
  readonly propertyName: string | null;
  readonly serviceCategoryId: string | null;
  readonly serviceName: string | null;
}

// ---------------------------------------------------------------------------
// Agreements
// ---------------------------------------------------------------------------

/**
 * How an agreement's property set was decided (ADR-04).
 *
 * The union used to be declared in `AgreementForm.tsx` and the wire enum spelled
 * its members out a second time in `agreementFieldsSchema` below — two lists of
 * the same three strings, in different packages. The schema now reads THIS
 * array, so the wire values are these values by construction.
 */
export const COVERAGE_TYPES = ['single', 'multi', 'company_wide'] as const;
export type CoverageType = (typeof COVERAGE_TYPES)[number];

/**
 * The words on the control. `company_wide` is the one that must never reach a
 * screen, and the coverage summary line (docs/04 §3 catalog) says "Applies
 * company-wide" for it in prose — which is a SENTENCE, not this label, and is
 * built by each screen from its own facts.
 */
export const COVERAGE_TYPE_LABEL: Record<CoverageType, string> = {
  single: 'One property',
  multi: 'Selected properties',
  company_wide: 'Company-wide',
};

/** The plain field set — refinements live on the write schema below, and the
 * update schema takes a partial of THIS (a refined schema has no .partial). */
export const agreementFieldsSchema = z.object({
  name: z.string().trim().min(1).max(200),
  coverageType: z.enum(COVERAGE_TYPES),
  /**
   * The snapshot input (ADR-04): the EXPLICIT property set this agreement
   * covers. Required non-empty unless company_wide, which stores none.
   */
  propertyIds: z.array(z.uuid()).max(1000).default([]),
  /** Display metadata: how the set was chosen ("selected via …"). */
  coverageSelector: z.record(z.string(), z.string()).nullish(),
  /**
   * ONE service per agreement (ADR-42). Required, because it is half of the
   * supersession key: without it "which agreement governs HVAC here" has no
   * answer, and the database index that enforces one-per-scope has nothing to
   * key on. A bundled contract becomes N agreements sharing one document.
   */
  serviceCategoryId: z.uuid(),
  startDate: z.iso.date().nullish(),
  endDate: z.iso.date().nullish(),
  autoRenew: z.boolean().default(false),
  renewNotes: z.string().trim().max(2000).nullish(),
  costCents: z.number().int().min(0).nullish(),
  billingFrequency: z.enum(BILLING_FREQUENCIES).nullish(),
  terms: z.string().trim().max(8000).nullish(),
});

export const agreementWriteSchema = agreementFieldsSchema
  .refine(
    (value) => value.coverageType === 'company_wide' || value.propertyIds.length > 0,
    'A single- or multi-property agreement must name at least one property',
  )
  .refine(
    (value) => value.coverageType !== 'company_wide' || value.propertyIds.length === 0,
    'A company-wide agreement stores no property rows — it applies everywhere',
  )
  .refine(
    (value) => value.coverageType !== 'single' || value.propertyIds.length === 1,
    'A single-property agreement covers exactly one property',
  );
export type AgreementWrite = z.infer<typeof agreementWriteSchema>;

export const agreementCreateSchema = z.object({
  vendorId: z.uuid(),
  agreement: agreementWriteSchema,
});

export const agreementUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(agreementFieldsSchema),
  /** docs/03 §2b: acknowledge the CONFIRM_REQUIRED effects and resubmit. */
  confirm: z.boolean().default(false),
  /**
   * Required only when the agreement is CLOSED HISTORY — superseded, ended,
   * discarded or expired (docs/11 F-89) — and then it goes into the audit event
   * (Matt 2026-08-25). Closed history is the record of what was true then;
   * changing it needs the key AND the sentence, because the key says who may
   * and the reason says why they did.
   */
  reason: z.string().trim().min(3).max(500).nullish(),
});

export interface AgreementDetail {
  readonly id: string;
  readonly vendorId: string;
  readonly vendorName: string;
  readonly name: string;
  readonly coverageType: 'single' | 'multi' | 'company_wide';
  readonly coverageSelector: Readonly<Record<string, string>> | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly autoRenew: boolean;
  readonly renewNotes: string | null;
  readonly costCents: number | null;
  readonly billingFrequency: BillingFrequency | null;
  readonly terms: string | null;
  readonly status: AgreementStatus;
  readonly supersededById: string | null;
  /**
   * The replacement's name, so the "edit its replacement" instruction can be a
   * LINK somebody reads before clicking. Returned beside the id because the id
   * alone had no consumer for weeks: the screen said where to go and offered no
   * way to get there.
   */
  readonly supersededByName: string | null;
  readonly version: number;
  /**
   * ONE FLAG PER ACTION, each computed from the key its route and operation
   * enforce (docs/11 F-89, CR-031) — never a create-OR-edit aggregate.
   *
   *   canEdit      agreements.edit, status draft/active, vendor active
   *   canAmend     agreements.amend_superseded, closed status, vendor active
   *   canActivate  agreements.supersede, status draft, vendor active
   *   canRenew     agreements.create, status active, vendor active
   *   canEnd       agreements.supersede, status active
   *   canDiscard   agreements.edit, status draft
   */
  readonly canEdit: boolean;
  readonly canAmend: boolean;
  /** A change needs a stated reason — exactly when the record is closed history. */
  readonly editNeedsReason: boolean;
  readonly canActivate: boolean;
  readonly canRenew: boolean;
  readonly canEnd: boolean;
  readonly canDiscard: boolean;
  readonly vendorStatus: 'active' | 'inactive';
  readonly serviceCategoryId: string;
  readonly serviceName: string;
  /** `active: false` marks a recorded property that has since been retired. */
  readonly properties: readonly {
    id: string;
    name: string;
    city: string | null;
    active: boolean;
  }[];
  /**
   * The ACTIVE agreements this draft could deliberately replace (same vendor,
   * same service, same kind of scope), each at the version it was read at —
   * from the SAME statement as this record. `conflicts` marks the ones this
   * draft overlaps; the activation must supersede the sole one, or end extras.
   */
  readonly eligibleIncumbents: readonly {
    id: string;
    name: string;
    version: number;
    coverageType: 'single' | 'multi' | 'company_wide';
    conflicts: boolean;
    /** Properties it covers that this draft does not — coverage that stops. */
    stops: readonly string[];
  }[];
  /** A company-wide agreement for the same vendor+service still in force, if any. */
  readonly companyWideFallback: string | null;
  /** Who ended or discarded it, when and why. */
  readonly ended: {
    kind: 'ended' | 'discarded';
    at: string;
    byName: string | null;
    reason: string;
  } | null;
}

/** POST /agreements/:id/end and /discard (docs/11 F-89). */
export const agreementCloseSchema = z.object({
  version: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});
export type AgreementClose = z.infer<typeof agreementCloseSchema>;

// ---------------------------------------------------------------------------
// Utility accounts
// ---------------------------------------------------------------------------

/**
 * A utility ACCOUNT — the unit of a utility relationship (ADR-53).
 *
 * Matt's own sketch, 2026-08-26:
 *
 *   vendor > Duke Energy (utility type: power) > Properties (or) Accounts
 *   >> 4500 Cameron Valley Parkway > Account: 98375908739-398734 >
 *      Account Information, Contract Information, Cost, Notes, Action Items
 *
 * and: "Essentially this is the same data/database references but just displayed
 * different." So there is ONE record — this one — and the vendor's Properties
 * view and Accounts view are two groupings of it. Nothing is duplicated, so
 * nothing can disagree.
 *
 * The CONTRACT lives here now (0054 retires 0042's agreement link). Matt's
 * layout puts "Contract Information" inside the account, and it removes the
 * app's second answer to "when does this contract run": a power bill and a
 * roofing contract had become structurally identical records. `agreements` means
 * SERVICE agreement, everywhere.
 */
export const utilityAccountWriteSchema = z.object({
  /**
   * REQUIRED, and it must be one of the VENDOR's ACTIVE allowed types (docs/11
   * F-66, F-88). The database proves membership with a composite FK to
   * `vendor_utility_types` (0054) and activity with 0101's admission trigger.
   * Not a service category: Matt, "utility vendors ... need to carry the
   * utility type (not a service type)".
   */
  utilityTypeId: z.uuid(),
  accountNumber: z.string().trim().min(1).max(120),
  /** The properties this account serves — one account can cover several. */
  propertyIds: z.array(z.uuid()).max(200).default([]),
  estMonthlyCostCents: z.number().int().min(0).nullish(),
  billingFrequency: z.enum(BILLING_FREQUENCIES).nullish(),
  portalUrl: z.union([z.literal(''), z.url()]).nullish(),
  primaryContactId: z.uuid().nullish(),
  /**
   * Contract information — all optional, and all-null is COMPLETE. Matt,
   * 2026-08-24: "not all utility accounts have contracts and agreements but this
   * is possible". A meter has no negotiated term.
   */
  contractStart: z.iso.date().nullish(),
  contractEnd: z.iso.date().nullish(),
  contractTerms: z.string().trim().max(4000).nullish(),
  autoRenew: z.boolean().default(false),
  renewNotes: z.string().trim().max(2000).nullish(),
  /**
   * How you get INTO the account. v1 carries both of these; v2 had neither.
   * The PIN is NOT here — it is a `security_codes` row (docs/01 §5), masked with
   * a permissioned, audited reveal, because it is exactly the class of value
   * that must never reach a list payload, an export or a log.
   */
  authorizedAccessNotes: z.string().trim().max(4000).nullish(),
  additionalAccessInfo: z.string().trim().max(4000).nullish(),
  /** ADR-37 — the Important Details band. */
  criticalInformation: z.string().trim().max(4000).nullish(),
  notes: z.string().trim().max(4000).nullish(),
  /**
   * NARROWER THAN docs/03 §2b's general cascade (docs/11 F-66, F-88). An account
   * save NEVER expands a vendor's populated type set — a type outside it is
   * refused (422 TYPE_NOT_ALLOWED), and `confirm: true` does not change that.
   * The one confirmation left is the legacy EMPTY set: choosing a type for the
   * first account of a vendor that has none adds it, atomically with the
   * account, once confirmed with `typeSetVersion`.
   */
  confirm: z.boolean().default(false),
  /**
   * The vendor's `utilityTypeSetVersion` as the person reviewed it — required
   * with `confirm: true` for the empty-set remediation, re-derived under the
   * set's lock, and refused (409 TYPE_SET_CHANGED) when the set moved since.
   */
  typeSetVersion: z.string().min(1).max(64).optional(),
});
export type UtilityAccountWrite = z.infer<typeof utilityAccountWriteSchema>;

export const utilityAccountUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(utilityAccountWriteSchema),
});

/**
 * A NEW UTILITY VENDOR AND ITS FIRST ACCOUNT, IN ONE SAVE (punch 5.2).
 *
 * Matt, 2026-09-01: "A new utility vendor must always be created with its first
 * utility account in the same atomic save, regardless of origin... Do not permit
 * a vendor-only utility record to be created temporarily."
 *
 * That last clause is why this is ONE endpoint rather than the two writes a
 * client could obviously make instead. A utility vendor with no account is not a
 * useful intermediate state — it is a vendor that can reach no property at all,
 * because a utility vendor reaches properties only through accounts (0081). A
 * client sequence that failed between the two writes would leave exactly that,
 * and docs/03 §5b's "a partial failure says what DID save" is the rule for
 * writes that are independently meaningful. These are not: the whole thing
 * commits or none of it does.
 *
 * ORIGIN. From a property, `originPropertyId` names the building the user was
 * standing on and MUST appear in the account's `propertyIds` — that association
 * is the point of creating the vendor from there. From the Vendors module the
 * field is absent and `propertyIds` may be empty: the account still exists, it
 * just does not serve anything yet.
 *
 * The origin is recorded in the AUDIT, not in a column. "How did this vendor get
 * here" is a history question, and a marker on the association would make the
 * origin property harder to remove later than every other one — the exact
 * asymmetry Matt ruled out ("the origin property cannot become permanently
 * undeletable").
 */
export const utilityVendorWithAccountSchema = z
  .object({
    /**
     * `vendorType` is absent by construction. The route pins it to 'utility';
     * accepting it would let a caller ask for a service vendor through the one
     * endpoint that cannot produce one.
     */
    vendor: vendorWriteSchema.omit({ vendorType: true }).strict(),
    account: utilityAccountWriteSchema,
    originPropertyId: z.uuid().optional(),
    createAnyway: z.boolean().default(false),
    createAnywayReason: z.string().trim().max(500).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.createAnyway && (value.createAnywayReason ?? '').length < CREATE_ANYWAY_REASON_MIN) {
      ctx.addIssue({
        code: 'custom',
        path: ['createAnywayReason'],
        message: 'Explain why this is not a duplicate',
      });
    }
    if (
      value.originPropertyId !== undefined &&
      !value.account.propertyIds.includes(value.originPropertyId)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['account', 'propertyIds'],
        message: 'The first account must serve the property it was created from',
      });
    }
  });
export type UtilityVendorWithAccount = z.infer<typeof utilityVendorWithAccountSchema>;

/**
 * The property-context notice (punch 5.2, Matt's answer to question 3).
 *
 * NOT a duplicate, and deliberately not shaped like one. The portfolio-wide name
 * check asks "does this company already exist"; this asks the different and
 * more useful question "does this BUILDING already buy this kind of utility from
 * somebody". Two power accounts at one property is legitimate — two meters, a
 * house account and a tenant account — so this never blocks and never asks for a
 * bypass reason. Making it blocking would train people to type a reason to get
 * past a warning that is usually correct to ignore, which is how the real
 * duplicate prompt stops being read.
 */
export interface PropertyUtilityNotice {
  readonly accountId: string;
  readonly accountNumber: string;
  readonly vendorId: string;
  readonly vendorName: string;
  readonly utilityTypeId: string;
  readonly typeName: string | null;
}

/**
 * Somebody the utility will talk to about this account.
 *
 * A LIST, not a paragraph: "is Dana still authorised on the Duke account" has a
 * row-shaped answer, and v1's own `authorized_users[]` had already concluded
 * that. `internalContactId` when they are in the directory, a typed name when
 * they are not — the utility's records may name an owner's accountant, or a
 * manager who left two years ago and is still on the account.
 *
 * A sub-resource with its own writes, per docs/03 §5: it saves on its own, so it
 * is a card on the record and never a field in the account's form.
 */
export const authorizedUserWriteSchema = z.object({
  internalContactId: z.uuid().nullish(),
  /**
   * Nullable, because a linked row does not need one: the screen shows the
   * directory's `full_name` for those, so a typed copy would be a second name
   * for one person that could drift. The server requires ONE of the two — the
   * rule is not expressible here without a refinement, and a refinement on this
   * object would not survive `partialUpdateSchema` below.
   */
  name: z.string().trim().min(1).max(200).nullish(),
  phone: z.string().trim().max(40).nullish(),
  email: z.union([z.literal(''), z.email()]).nullish(),
  notes: z.string().trim().max(2000).nullish(),
});
export type AuthorizedUserWrite = z.infer<typeof authorizedUserWriteSchema>;

export const authorizedUserUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(authorizedUserWriteSchema),
});

export interface AuthorizedUserRow {
  readonly id: string;
  readonly internalContactId: string | null;
  readonly name: string;
  readonly phone: string | null;
  readonly email: string | null;
  readonly notes: string | null;
  readonly status: 'active' | 'inactive';
  readonly version: number;
}

export interface UtilityAccountSummary {
  readonly id: string;
  readonly accountNumber: string;
  readonly typeName: string | null;
  readonly status: 'active' | 'inactive';
  readonly estMonthlyCostCents: number | null;
  readonly billingFrequency: BillingFrequency | null;
  readonly contractEnd: string | null;
  readonly propertyCount: number;
  /** Named, not counted: "Cameron Valley" is the answer; "1" is a riddle. */
  readonly propertyNames: readonly string[];
  readonly version: number;
}

/**
 * The vendor's PROPERTIES view — the same accounts, grouped the other way.
 *
 * Matt, 2026-08-26: "Essentially this is the same data/database references but
 * just displayed different." So this is a projection of `utility_accounts`, not
 * a second store: an account covering three buildings appears once under
 * Accounts and three times here, and there is nothing that could disagree.
 */
export interface VendorUtilityPropertyRow {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly city: string | null;
  readonly accounts: readonly {
    readonly id: string;
    readonly accountNumber: string;
    readonly typeName: string | null;
    readonly estMonthlyCostCents: number | null;
    readonly contractEnd: string | null;
    readonly status: 'active' | 'inactive';
    /** True when this account also serves other buildings. */
    readonly sharedWithOthers: boolean;
  }[];
}

export interface UtilityAccountDetail {
  readonly id: string;
  readonly vendorId: string;
  readonly vendorName: string;
  /**
   * The VENDOR's lifecycle, not the account's (RM-047). A retired vendor
   * accepts no edits anywhere beneath it, so the account screen has to know —
   * `canManage` answers "may this person write accounts", which is a different
   * question and was the only one being asked.
   */
  readonly vendorStatus: 'active' | 'inactive';
  readonly utilityTypeId: string | null;
  readonly typeName: string | null;
  /**
   * The account's type in the catalog (docs/11 F-88): false is a RETIRED type
   * kept as this account's history, shown by name and marked; null is no type.
   */
  readonly typeActive: boolean | null;
  readonly accountNumber: string;
  readonly estMonthlyCostCents: number | null;
  readonly billingFrequency: BillingFrequency | null;
  readonly portalUrl: string | null;
  readonly primaryContactId: string | null;
  readonly primaryContactName: string | null;
  /** Contract information, on the account itself since 0054. All null = a meter. */
  readonly contractStart: string | null;
  readonly contractEnd: string | null;
  readonly contractTerms: string | null;
  readonly autoRenew: boolean;
  readonly renewNotes: string | null;
  readonly authorizedAccessNotes: string | null;
  readonly additionalAccessInfo: string | null;
  readonly criticalInformation: string | null;
  readonly notes: string | null;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  readonly canManage: boolean;
  readonly authorizedUsers: readonly AuthorizedUserRow[];
  readonly properties: readonly { id: string; name: string; city: string | null }[];
}
